import { COPY_TOOL_CONCURRENCY } from 'constants/appConstants';
import { UNIQUE_DEVICE_ID } from 'constants/authConstants';
import {
  CopyMode,
  ICopyPaddockDataRequest,
  ICopyTarget,
} from 'interfaces/ICopyPaddockData';
import { GUnit } from 'interfaces/IGlobalCatalogue';
import { IInput } from 'interfaces/IInput';
import { IPaddock } from 'interfaces/IPaddock';
import { PartialUserProfile } from 'interfaces/IUserProfile';
import { IZone } from 'interfaces/IZone';
import { buildBlankInput } from 'pages/FarmsPage/CreatePlanModal/buildBlankInput';
import InputService from 'services/InputService';
import ZoneService from 'services/ZoneService';
import { getInputYear } from 'utils/FarmUtils';
import { runWithConcurrency } from 'utils/runWithConcurrency';
import { getUserDisplayName } from 'utils/UserUtils';

import {
  buildAddedToInput,
  buildCopiedTasks,
  buildNewCopyInput,
  buildReplacedInput,
  getCopySourceYear,
  getCopyYearOffset,
  getExistingTargetId,
} from './buildCopiedInput';

/** What a copy needs besides the request; the source is read, never written. */
export interface CopyPaddockDataContext {
  source: IInput;
  paddocksById: Map<string, IPaddock>;
  /** The org's zones by id; a zone missing here is fetched. */
  zonesById: Map<string, IZone>;
  gUnits: GUnit[];
  userProfile?: PartialUserProfile;
  /** Called with each saved document and its destination as it lands, e.g. to update the caches. */
  onSaved: (input: IInput, target: ICopyTarget) => void;
  /** Called once per destination after its save and follow-up, saying whether it saved. */
  onTargetDone?: (target: ICopyTarget, isCopied: boolean) => void;
  /** Checked before each destination; false (e.g. signed out) skips the rest as not copied. */
  shouldContinue?: () => boolean;
}

export interface CopyPaddockDataResult {
  copiedCount: number;
  /** The destinations that weren't copied, in request order. */
  failedTargets: ICopyTarget[];
}

const getTargetZone = async (
  paddock: IPaddock,
  zonesById: Map<string, IZone>
): Promise<IZone> =>
  zonesById.get(paddock.zoneId) ?? ZoneService.GetById(paddock.zoneId);

const getDeviceId = (): string | null => {
  try {
    return localStorage.getItem(UNIQUE_DEVICE_ID);
  } catch {
    return null;
  }
};

/** Stamped as Planning stamps its saves, so Last saved and the audit trail show the copy. */
const stampModified = (
  input: IInput,
  userProfile?: PartialUserProfile
): IInput => {
  const modifierName = getUserDisplayName(
    userProfile?.firstName,
    userProfile?.lastName
  );
  return {
    ...input,
    sourceId: getDeviceId() ?? input.sourceId,
    dateModified: new Date().toISOString(),
    modifierId: userProfile?.id ?? input.modifierId ?? null,
    snapModifier: modifierName || input.snapModifier || null,
  };
};

/** A new document in the target's paddock, made with Create. */
const copyToNewDocument = async (
  request: ICopyPaddockDataRequest,
  target: ICopyTarget,
  sourceYear: number | null,
  context: CopyPaddockDataContext
): Promise<IInput> => {
  const { source, paddocksById, zonesById, gUnits, userProfile } = context;
  const paddock = paddocksById.get(target.paddockId);
  if (!paddock) throw new Error('Destination paddock not found');
  const blank = buildBlankInput({
    paddock,
    zone: await getTargetZone(paddock, zonesById),
    gUnits,
    orgId: request.orgId,
    userProfile,
  });
  const yearOffset = getCopyYearOffset(sourceYear, request.destination.year);
  const tasks = buildCopiedTasks({
    source,
    sourceMode: request.source.mode,
    filter: request.source.filter,
    destinationMode: request.destination.mode,
    destinationZone: blank.zone,
    options: request.options,
    yearOffset,
    gUnits,
    userProfile,
  });
  // The blank one's sourceId is the device id's storage key, so it's stamped like an update.
  const input = stampModified(
    buildNewCopyInput(
      blank,
      source,
      tasks,
      yearOffset,
      request.destination.year
    ),
    userProfile
  );
  const response = await InputService.CreateDoc(
    input,
    new Date().toISOString()
  );
  return response?.data?.[0] ?? input;
};

/** Replace or Add to on an existing document, read fresh so newer edits aren't overwritten. */
const copyToExistingDocument = async (
  request: ICopyPaddockDataRequest,
  inputId: string,
  sourceYear: number | null,
  context: CopyPaddockDataContext
): Promise<IInput> => {
  const { source, gUnits, userProfile } = context;
  const destination = await InputService.GetById(inputId);
  // An undated document shows in every year, so it takes the year it was copied into.
  const yearOffset = getCopyYearOffset(
    sourceYear,
    getInputYear(destination) ?? request.destination.year
  );
  const tasks = buildCopiedTasks({
    source,
    sourceMode: request.source.mode,
    filter: request.source.filter,
    destinationMode: request.destination.mode,
    destinationZone: destination.zone,
    options: request.options,
    yearOffset,
    gUnits,
    userProfile,
  });
  const input = stampModified(
    request.copyMode === CopyMode.Replace
      ? buildReplacedInput(destination, source, tasks, request.destination.mode)
      : buildAddedToInput(
          destination,
          source,
          tasks,
          request.source.filter.includeCropAndVariety
        ),
    userProfile
  );
  const response = await InputService.Update(input, new Date().toISOString());
  return response?.data?.[0] ?? input;
};

/**
 * Copies the source's data into the destinations, a few at once: Replace and
 * Add to update the ticked document, and a paddock with no document (or any
 * paddock in Add as new) gets a new one. A destination that fails is
 * reported and the rest carry on. The source document is never written.
 */
export const copyPaddockData = async (
  request: ICopyPaddockDataRequest,
  context: CopyPaddockDataContext
): Promise<CopyPaddockDataResult> => {
  const failed = new Set<ICopyTarget>();
  let copiedCount = 0;
  // The same for every destination, so it's worked out once.
  const sourceYear = getCopySourceYear(
    context.source,
    request.source.mode,
    request.source.filter
  );
  const notifyDone = (target: ICopyTarget, isCopied: boolean) => {
    try {
      context.onTargetDone?.(target, isCopied);
    } catch {
      // Progress only; the result below still says what saved.
    }
  };
  // A few at once, so a large copy is quick without flooding the API.
  await runWithConcurrency(
    request.destination.targets,
    COPY_TOOL_CONCURRENCY,
    async (target) => {
      const existingId = getExistingTargetId(target, request.copyMode);
      if (context.shouldContinue && !context.shouldContinue()) {
        failed.add(target);
        notifyDone(target, false);
        return;
      }
      let saved: IInput;
      try {
        saved =
          existingId === null
            ? await copyToNewDocument(request, target, sourceYear, context)
            : await copyToExistingDocument(
                request,
                existingId,
                sourceYear,
                context
              );
      } catch {
        failed.add(target);
        notifyDone(target, false);
        return;
      }
      // Saved, so it counts as copied even if the follow-up fails; a retry would copy it twice.
      copiedCount += 1;
      try {
        context.onSaved(saved, target);
      } catch {
        // The caches catch up on their next refetch.
      }
      notifyDone(target, true);
    }
  );
  return {
    copiedCount,
    failedTargets: request.destination.targets.filter((target) =>
      failed.has(target)
    ),
  };
};
